import React from 'react';
import type { TransformedReport } from '../types';
import AvatarSvg, { SEGMENT_COLORS } from './AvatarSvg';

interface InsightSlideProps {
  data: TransformedReport;
  segmentIndex: number;
}

/**
 * InsightSlide - Slide 9: Persona card + 3 Reasons (insights deep dive).
 */
const InsightSlide: React.FC<InsightSlideProps> = ({ data, segmentIndex }) => {
  const segments = data.segments ?? [];
  const seg = segments[segmentIndex];

  if (!seg) return null;

  // Build 3 reasons from emotion_map
  const reasons = [
    { label: 'Reason 01', text: seg.emotion_map?.pain || '' },
    { label: 'Reason 02', text: seg.emotion_map?.barriers || '' },
    { label: 'Reason 03', text: seg.emotion_map?.purchase_drivers || '' },
  ].filter((r) => r.text);

  // Get detail text for reasons (from benefits as fallback)
  const details = [
    seg.benefits?.functional || '',
    seg.benefits?.emotional || '',
    seg.benefits?.self_actualization || '',
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div className="slide-hdr">
        <p className="sl">Step 03 — Insight</p>
        <h2>
          {segments.length > 1 ? `${segmentIndex + 1}` : ''}
          {seg.name}のインサイトの深掘り
        </h2>
        <p className="bt">
          {seg.emotion_map?.ideal_state
            ? `「${seg.emotion_map.ideal_state}」——踏み出せない理由を整理しました`
            : 'ターゲットの心理的バリアを整理しました'}
        </p>
      </div>

      <div style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
        <div style={{ display: 'flex', flexDirection: 'row', gap: 'clamp(12px, 1.8vmin, 24px)', width: '100%' }}>
          {/* Left: Persona card */}
          <div
            className="card"
            style={{
              width: 'clamp(160px, 22vmin, 280px)',
              flexShrink: 0,
              padding: 'clamp(12px, 2vmin, 24px)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
            }}
          >
            <div style={{ width: 'clamp(56px, 8vmin, 96px)', height: 'clamp(56px, 8vmin, 96px)', borderRadius: '50%', background: '#E4E0ED', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 'clamp(8px, 1.2vmin, 16px)' }}>
              <AvatarSvg variant={segmentIndex} color={SEGMENT_COLORS[segmentIndex]} className="w-12 h-12" />
            </div>
            <span className="pill" style={{ marginBottom: 'clamp(6px, 1vmin, 12px)' }}>
              {seg.funnel_stage || `Segment ${segmentIndex + 1}`}
            </span>
            <p style={{ fontSize: 'clamp(16px, 2.4vmin, 20px)', fontWeight: 700, lineHeight: 1.4, marginBottom: 'clamp(6px, 1vmin, 12px)' }}>
              {seg.name}
            </p>
            <div style={{ width: 32, height: 2, background: '#E4E0ED', margin: '0 auto clamp(6px, 1vmin, 12px)' }} />
            <p style={{ fontSize: 'clamp(11px, 1.4vmin, 13px)', color: 'rgba(29,25,38,0.4)', lineHeight: 1.6 }}>
              属性：{seg.demographics?.age_range || ''} / {seg.demographics?.occupation || ''}
            </p>
          </div>

          {/* Right: 3 Reasons */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 'clamp(8px, 1.2vmin, 16px)' }}>
            {reasons.map((reason, i) => (
              <div
                key={i}
                style={{
                  borderLeft: '4px solid #1D1926',
                  paddingLeft: 'clamp(12px, 2vmin, 20px)',
                  paddingTop: 'clamp(6px, 1vmin, 12px)',
                  paddingBottom: 'clamp(6px, 1vmin, 12px)',
                }}
              >
                <p className="sl" style={{ fontSize: 11, marginBottom: 4 }}>{reason.label}</p>
                <p style={{ fontSize: 'clamp(16px, 2.4vmin, 22px)', fontWeight: 700, lineHeight: 1.4, marginBottom: 'clamp(4px, 0.6vmin, 8px)' }}>
                  「{reason.text}」
                </p>
                {details[i] && (
                  <p style={{ fontSize: 'clamp(12px, 1.6vmin, 14px)', color: 'rgba(29,25,38,0.6)', lineHeight: 1.7 }}>
                    {details[i]}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InsightSlide;
